import React, { useEffect, useMemo } from "react";

const formatAmount = (amount) => {
  const value = Number(amount ?? 0);

  return Number.isFinite(value) ? value.toFixed(2) : "0.00";
};

const getCustomerName = (order) => {
  return (
    order?.customer_name ||
    order?.customerName ||
    [
      order?.orderUser?.firstName,
      order?.orderUser?.lastName,
    ]
      .filter(Boolean)
      .join(" ") ||
    "--"
  );
};

const getItemName = (item) => {
  return (
    item?.name ||
    item?.item_name ||
    item?.product_name ||
    item?.productName ||
    "Unnamed Item"
  );
};

const getItemQuantity = (item) => {
  return (
    item?.qty ??
    item?.quantity ??
    item?.item_quantity ??
    0
  );
};

const getItemExtras = (item) => {
  const extras =
    item?.items_extra_options ??
    item?.item_extra_options ??
    item?.extra_options ??
    item?.extras ??
    item?.options;

  if (extras == null || extras === "") {
    return "--";
  }

  if (typeof extras === "string") {
    return extras;
  }

  if (Array.isArray(extras)) {
    return extras
      .map((extra) => {
        if (typeof extra === "string") {
          return extra;
        }

        return (
          extra?.name ||
          extra?.option_name ||
          extra?.title ||
          ""
        );
      })
      .filter(Boolean)
      .join(", ") || "--";
  }

  if (typeof extras === "object") {
    return (
      extras.name ||
      extras.option_name ||
      extras.title ||
      JSON.stringify(extras)
    );
  }

  return String(extras);
};

const getItemPrice = (item) => {
  return (
    item?.unitPrice ??
    item?.unit_price ??
    item?.price ??
    item?.item_price ??
    0
  );
};

const getOrderTotal = (order) => {
  return (
    order?.FinalAmount ??
    order?.finalAmount ??
    order?.total_amount ??
    order?.totalAmount ??
    order?.total ??
    0
  );
};

const getSurcharges = (order) => {
  const surcharges =
    order?.appliedSurchangeAmount ??
    order?.appliedSurchargeAmount ??
    order?.surcharges ??
    [];

  return Array.isArray(surcharges) ? surcharges : [];
};

const OfflineOrderDetails = ({ orderId, orders = [],  displayOrders = []}) => {
  // Find the selected order directly in the locally loaded orders.
  // No backend API request is made.
  const orderDetailsData = useMemo(() => {
  if (orderId == null) {
    return null;
  }

  const matchesOrder = (order) => {
    if (!order) return false;

    const ids = [
      order.id,
      order.localOrderId,
      order.orderid,
      order.orderNumber,
    ];

    return ids.some(
      (id) =>
        id != null &&
        String(id) === String(orderId)
    );
  };

  // Prefer displayOrders because it may contain
  // the fields already prepared for the main page.
  const displayOrder = Array.isArray(displayOrders)
    ? displayOrders.find(matchesOrder)
    : null;

  if (displayOrder) {
    return displayOrder;
  }

  // Fall back to the original local orders.
  const localOrder = Array.isArray(orders)
    ? orders.find(matchesOrder)
    : null;

  return localOrder || null;
}, [orderId, orders, displayOrders]);

  const items = Array.isArray(orderDetailsData?.items)
    ? orderDetailsData.items
    : [];

  const surcharges = getSurcharges(orderDetailsData || {});

  const customerName = orderDetailsData
    ? getCustomerName(orderDetailsData)
    : "--";

  const customerPhone =
    orderDetailsData?.customer_phone ??
    orderDetailsData?.customerPhone ??
    orderDetailsData?.orderUser?.contactNumber ??
    "--";

  const customerAddress =
    orderDetailsData?.customer_address ??
    orderDetailsData?.customerAddress ??
    orderDetailsData?.address ??
    "--";

  const orderTotal = getOrderTotal(orderDetailsData || {});

  const orderNumber =
    orderDetailsData?.orderid ??
    orderDetailsData?.orderNumber ??
    orderDetailsData?.localOrderId ??
    orderDetailsData?.id ??
    "--";

  return (
    <div
      className="modal modalStyle fade"
      id="orderDetailModal"
      tabIndex="-1"
      aria-labelledby="offlineOrderDetailModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5
              className="modal-title mdTitle"
              id="offlineOrderDetailModalLabel"
            >
              Offline Order {orderNumber} Details
            </h5>

            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>

          <div className="modal-body">
            {!orderDetailsData ? (
              <div className="text-center py-4">
                <p className="mb-0">
                  {orderId == null
                    ? "Select an order to view its details."
                    : "Order details not found in local storage."}
                </p>
              </div>
            ) : (
              <>
                {/* Order Items */}
                <div className="tableSection">
                  <div className="table-responsive">
                    <table className="table tableStyle align-middle">
                      <thead>
                        <tr>
                          <th colSpan="2">Item Name</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th className="text-nowrap">
                            Item Extra Options
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {items.length > 0 ? (
                          items.map((item, index) => (
                            <tr
                              key={
                                item?.id ??
                                item?.item_id ??
                                `${orderNumber}-item-${index}`
                              }
                            >
                              <td className="tableProImgCol">
                               <img
                                src={
                                  item?.first_image_url ||
                                  item?.image ||
                                  item?.imageUrl ||
                                  "/images/product-img-1.webp"
                                }
                                alt={getItemName(item)}
                                className="tableProImg"
                                onError={(event) => {
                                  event.currentTarget.onerror = null;
                                  event.currentTarget.src =
                                    "/images/product-img-1.webp";
                                }}
                              />
                              </td>

                              <td>
                                <span className="proName d-block">
                                  {getItemName(item)}
                                </span>

                               {(item?.sizeName || item?.size_name || item?.size) && (
                                  <span className="proDetail">
                                    {item.sizeName || item.size_name || item.size}
                                  </span>
                                )}
                              </td>

                              <td>{getItemQuantity(item)}</td>

                              <td>
                                $
                                {formatAmount(getItemPrice(item))}
                              </td>

                              <td>
                                {getItemExtras(item)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="5"
                              className="text-center py-4"
                            >
                              No items found for this order.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Subtotal */}
                  {orderDetailsData.subtotal != null && (
                    <div className="sbRow">
                      <span className="totalLbl">
                        Subtotal
                      </span>

                      <span className="totalVal">
                        $
                        {formatAmount(
                          orderDetailsData.subtotal
                        )}
                      </span>
                    </div>
                  )}

                  {/* Tax */}
                  {orderDetailsData.tax != null && (
                    <div className="sbRow">
                      <span className="totalLbl">
                        Tax
                      </span>

                      <span className="totalVal">
                        $
                        {formatAmount(orderDetailsData.tax)}
                      </span>
                    </div>
                  )}

                  {/* Discount */}
                  {Number(orderDetailsData.discount ?? 0) > 0 && (
                    <div className="sbRow">
                      <span className="totalLbl">
                        Discount
                      </span>

                      <span className="totalVal">
                        -$
                        {formatAmount(
                          orderDetailsData.discount
                        )}
                      </span>
                    </div>
                  )}

                  {/* Surcharges */}
                  {surcharges.map((surcharge, index) => (
                    <div
                      className="sbRow"
                      key={`surcharge-${index}`}
                    >
                      <span className="totalLbl">
                        {surcharge?.charge_name ||
                          surcharge?.name ||
                          "Surcharge"}
                      </span>

                      <span className="totalVal">
                        $
                        {formatAmount(
                          surcharge?.surcharge_amount ??
                            surcharge?.amount ??
                            0
                        )}
                      </span>
                    </div>
                  ))}

                  {/* Loyalty Discount */}
                  {Number(
                    orderDetailsData.loyaltyPointRedeemed
                      ?.amount ?? 0
                  ) > 0 && (
                    <div className="sbRow">
                      <span className="totalLbl">
                        Loyalty Discount
                      </span>

                      <span className="totalVal">
                        $
                        {formatAmount(
                          orderDetailsData.loyaltyPointRedeemed
                            .amount
                        )}
                      </span>
                    </div>
                  )}

                  {/* Offer */}
                  {Number(
                    orderDetailsData.offer_amount ?? 0
                  ) > 0 && (
                    <div className="sbRow">
                      <span className="totalLbl">
                        Offer
                      </span>

                      <span className="totalVal">
                        $
                        {formatAmount(
                          orderDetailsData.offer_amount
                        )}
                      </span>
                    </div>
                  )}

                  {/* Total */}
                  <div className="sbRow">
                    <span className="totalLbl">
                      <strong>Total</strong>
                    </span>

                    <span className="totalVal">
                      <strong>
                        ${formatAmount(orderTotal)}
                      </strong>
                    </span>
                  </div>

                  {/* Payment Information */}
                  <div className="sbRow mt-2">
                    <span className="totalLbl">
                      Payment Status
                    </span>

                    <span className="totalVal">
                      {(() => {
                        const status = String(
                          orderDetailsData.status ??
                          orderDetailsData.orderstatus ??
                          ""
                        ).toUpperCase();

                        if (
                          status === "PAID" ||
                          Number(orderDetailsData.paid) === 1 ||
                          orderDetailsData.paid === true
                        ) {
                          return "Paid";
                        }

                        if (status === "UNPAID") {
                          return "Unpaid";
                        }

                        return "Unknown";
                      })()}
                    </span>
                  </div>
                </div>

                {/* Customer Details */}
                <div className="mt-3 mt-xl-4 mb-3 mb-lg-4 cardStyle">
                  <div className="cardHeader">
                    <h4 className="smTitle primaryColor mb-0">
                      Customer Details
                    </h4>
                  </div>

                  <div className="cardBody">
                    <div className="row gx-4 gy-3">
                      <div className="col-auto">
                        <div className="sbRow gap-2">
                          <span className="cLbl fw-semibold">
                            Name:
                          </span>

                          <span className="cVal">
                            {customerName}
                          </span>
                        </div>
                      </div>

                      <div className="col-auto">
                        <div className="sbRow gap-2">
                          <span className="cLbl fw-semibold">
                            Ph. No:
                          </span>

                          <span className="cVal">
                            {customerPhone}
                          </span>
                        </div>
                      </div>

                      <div className="col-auto">
                        <div className="sbRow gap-2">
                          <span className="cLbl fw-semibold">
                            Address:
                          </span>

                          <span className="cVal">
                            {customerAddress}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfflineOrderDetails;